import { InputParameterHierarchyType } from '../model'
import { TemplateInputParameters } from '../../../models/AiFeature'
import { CustomDropdownItemOption, DropDownItemOptionComponentProps, SelectDropdown, SelectOption } from '../../SelectDropdown/SelectDropdown'
import React, { useEffect, useState } from 'react'
import { useInputParametersUpdateScope } from '../hooks'
import { useUserTemplateConfigForm } from '../../../views/Setup/AIFeaturePresets/PresetConfig/Template/Hierarchy/hooks'
import { YesNoCancelModal } from '../../Modal/YesNoCancelModal'
import { TemplateBox } from '../../TemplateBox/TemplateBox'
import { Button } from '../../Button/Button'
import { ScopedBlock } from '../../ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../ScopedBlock/models'

interface TeamMemberPresetConfigFormBodyProps {
  type: InputParameterHierarchyType
  typeId: string
  inputParametersSchema: { [key: string]: any } | undefined
  defaultInputParameters: TemplateInputParameters
  parentInputParameters: TemplateInputParameters
  inputParameters: TemplateInputParameters
  showCustomizationSetting: boolean
  isCustomized: boolean
  updateLoading: boolean
  onSave: (inputParameters: { [key: string]: any }, isCustomized: boolean) => Promise<void>
  onHide: () => void
  userFilterValues: SelectOption[]
  selectedUserFilterOption: SelectOption | null
  setSelectedUserFilterOption: (newSelectedOption: SelectOption) => void
  ipHierarchyEnabled: boolean
}

export const TeamMemberPresetConfigFormBody: React.FC<TeamMemberPresetConfigFormBodyProps> = ({
  typeId,
  inputParameters,
  defaultInputParameters,
  parentInputParameters,
  showCustomizationSetting,
  isCustomized,
  updateLoading,
  onSave,
  onHide,
  userFilterValues,
  selectedUserFilterOption,
  setSelectedUserFilterOption,
  ipHierarchyEnabled,
}) => {
  const { hasIpHierarchyUpdateScope } = useInputParametersUpdateScope(selectedUserFilterOption?.id)
  const disabled = !ipHierarchyEnabled || !hasIpHierarchyUpdateScope

  const {
    editedInputParameters,
    templateHasValue,
    isChanged,
    customized,
    showConfirmation,
    shouldReset,
    setEditedInputParameters,
    setShowConfirmation,
    handleChangeCustomized,
    handleChangeTemplate,
    handleClickBack,
    handleClickCancel,
    handleClickSave,
    checkForChange,
  } = useUserTemplateConfigForm(inputParameters, parentInputParameters, isCustomized, onSave, onHide, disabled)

  const [changeToUser, setChangeToUser] = useState<SelectOption | null>(null)

  const handleUserSelected = (selectOption: SelectOption) => {
    if (isChanged) {
      setShowConfirmation(true)
      setChangeToUser(selectOption)
    } else {
      setSelectedUserFilterOption(selectOption)
    }
    return !isChanged
  }

  useEffect(() => {
    if (!selectedUserFilterOption) {
      return
    }
    checkForChange()
  }, [checkForChange, selectedUserFilterOption])

  const TeamMemberDropdownItemOption: React.FC<DropDownItemOptionComponentProps> = ({ onClick, optionId, optionLabel }) => {
    const selectOption = { id: optionId, label: optionLabel }
    const handleClick = (e: React.MouseEvent<HTMLElement>) => {
      const shouldProceed = handleUserSelected(selectOption)
      if (shouldProceed) {
        onClick(e)
      }
    }
    return (
      <CustomDropdownItemOption role='button' onClick={handleClick}>
        {optionLabel}
      </CustomDropdownItemOption>
    )
  }

  return (
    <>
      <YesNoCancelModal
        show={showConfirmation}
        onHide={() => {
          setShowConfirmation(false)
          setChangeToUser(null)
        }}
        message={'Do you want to save your changes?'}
        onYes={async () => {
          await onSave(editedInputParameters, customized)
          if (changeToUser) {
            setSelectedUserFilterOption(changeToUser)
            setChangeToUser(null)
            setShowConfirmation(false)
          } else {
            onHide()
          }
        }}
        onNo={() => {
          setEditedInputParameters(inputParameters)
          if (changeToUser) {
            setSelectedUserFilterOption(changeToUser)
            setChangeToUser(null)
            setShowConfirmation(false)
          } else {
            onHide()
          }
        }}
      />
      <div className='d-flex flex-column gap-4'>
        <div>
          <div className='mb-1 paragraph-medium-extended'>Choose team member</div>
          <SelectDropdown
            placeholder='Select a team member'
            options={userFilterValues}
            onOptionSelected={handleUserSelected}
            initialSelectedOption={selectedUserFilterOption}
            searchable
            DropDownItemOptionComponent={TeamMemberDropdownItemOption}
          />
        </div>
        {selectedUserFilterOption ? (
          <TemplateBox
            shouldShowGenerate={true}
            onGenerate={() => {}}
            template={inputParameters.template}
            defaultTemplate={defaultInputParameters.template}
            parentTemplate={parentInputParameters.template}
            generatedTemplate={''}
            onChangeTemplate={handleChangeTemplate}
            customized={customized}
            onChangeCustomized={handleChangeCustomized}
            showCustomizationSetting={showCustomizationSetting}
            shouldReset={shouldReset}
            disabled={disabled}
            typeId={typeId}
          />
        ) : null}
      </div>
      <div className='w-100 d-flex justify-content-between zocks-mt-3'>
        <Button variant='borderless' onClick={handleClickBack}>
          Close
        </Button>
        {hasIpHierarchyUpdateScope && (
          <div className='d-flex gap-2'>
            <>
              {ipHierarchyEnabled && (
                <Button variant='secondary' onClick={handleClickCancel} disabled={!isChanged} loading={updateLoading}>
                  Cancel
                </Button>
              )}
              <ScopedBlock
                missingScopeAction={MissingScopeAction.UPGRADE}
                quota={{
                  quota: 'ai.ipHierarchy.value',
                }}
              >
                <Button variant='primary' onClick={handleClickSave} disabled={ipHierarchyEnabled && (!isChanged || !templateHasValue)} loading={updateLoading}>
                  {ipHierarchyEnabled ? 'Save' : 'Get feature'}
                </Button>
              </ScopedBlock>
            </>
          </div>
        )}
      </div>
    </>
  )
}
